import type { Logger } from "@expert/logging";
import { getLogger } from "@expert/logging";
import type { Draft } from "immer";
import { configure } from "safe-stable-stringify";
import type { WritableKeysOf } from "type-fest";
import { create } from "zustand";
import type { StorageValue } from "zustand/middleware";
import { devtools, persist } from "zustand/middleware";
import { immer } from "zustand/middleware/immer";
import type { Task } from "../agent/task";
import { isTrainingTask, type TrainingTask } from "../agent/trainingTask";
import type { TaskStatus, TaskStatusReason, WrappingState } from "../agent/types";
import type { ConferenceDetails, ConferenceParticipant, VoiceTask } from "../agent/voice";
import { isVoiceTask } from "../agent/voice";
import { SessionInstance } from "./session";
import type { Session, SessionChangeReason } from "./types";

const safeStringify = configure({
    deterministic: false,
    maximumDepth: 10,
    strict: false,
});
const logger = getLogger({
    module: "SessionStore",
});

interface SessionStoreState {
    initialized: boolean;
    selectedSessionId: string;
    idleSession: SessionInstance;
    sessions: SessionInstance[];
}

export interface SessionStore extends SessionStoreState {
    /** Transactionally mutate a store with the immer WriteableDraft running the callers logic */
    mutateStore: (mutation: (state: Draft<SessionStore>) => void) => void;

    /** Ends the provided session and inserts the new session */
    addSession: (session: SessionInstance, shouldSelect?: boolean) => void;
    endSession: (sessionId: string, reason: SessionChangeReason) => void;
    setSelectedSession: (sessionId: string) => void;
    clearSelectedSession: () => void;
    addTaskToSession: (sessionId: string, task: Task) => void;
    updateTaskStatus: (taskId: string, status: TaskStatus, reason?: TaskStatusReason) => void;
    muteTask: (taskId: string, shouldMute: boolean) => void;
    setHold: (taskId: string, onHold: boolean) => void;
    setStartTimestamp: (taskId: string, timestamp: number) => void;
    setWrappingState: (wrappingState: WrappingState, sessionId: string) => void;
    clearWrappingState: (sessionId: string) => void;
    setConferenceStarted: (taskId: string, isStarted: boolean) => void;
    setCustomerLeftTheCall: (taskId: string) => void;
    upsertConferenceParticipant: (taskId: string, participant: ConferenceParticipant) => void;
    updateConferenceParticipant: (taskId: string, participantCallId: string, options: { hold: boolean }) => void;
    removeConferenceParticipant: (taskId: string, participantCallId: string) => void;
    resetConferenceState: (taskId: string, conferenceDetails: ConferenceDetails) => void;

    /*
    TODO:// Better way to write it will be `setTaskFromPartialTask`, that create a new task instance base on existing one,
    // maybe using a mix of `Partial<Task>` and type-fest `WritableKeysOf` to limit the edit to keys that are not readonly.
    // See below:
    https://github.com/sindresorhus/type-fest/blob/main/source/writable-keys-of.d.ts
    At the moment and due to time constraints, going with this implementation below.
    */
    setTaskKey: <T extends Task, K extends WritableKeysOf<T>>(taskId: string, key: K, value: T[K]) => void;

    /** Will clear the session history, preserving the current session */
    resetHistory: () => void; // TODO: remove when we remove history
    clearInactiveSession: (sessionId: string) => void;

    /** Will completely reset the store, clearing all sessions, and putting the store in an un-initialized state.
     * This is handy for unit tests, but should not be used in production.
     */
    resetStore: () => void;

    getSession: (sessionId: string) => Session | null;
    getSessionByTaskId: (taskId: string) => Session | null;
    getTask: (taskId: string) => Task | null;
    computed: {
        selectedSession: () => SessionInstance;
        selectedTasks: () => Task[]; // TODO: Temp backwards compat
        sessionIds: () => string[];
        isIdleSession: () => boolean;
    };
}

/** Prevent action exceptions from bubbling up to the rest of the application
 * Without having a try/catch inside every action
 */
function safeAction<T extends unknown[], R>(actionName: string, action: (...args: T) => R): (...args: T) => void {
    return (...args: T): void => {
        try {
            action(...args);
        } catch (error: unknown) {
            logger.error({ error, actionName }, "Error in SessionStore action");
        }
    };
}

export function createIdleSession() {
    const session = new SessionInstance({
        id: crypto.randomUUID(),
        startedTimestamp: Date.now(),
        kind: "without-customer",
        tasks: new Map(),
        wrappingState: null,
        metadata: {
            startReason: "InitialSession",
            status: "active",
        },
    });
    logger.info({ sessionId: session.id, kind: session.kind }, "Idle session created");
    return session;
}

export const useSessionStore = create<SessionStore>()(
    immer(
        persist(
            devtools(
                (set, get) => ({
                    initialized: false,
                    sessions: [],
                    idleSession: createIdleSession(),
                    selectedSessionId: "",
                    mutateStore: safeAction("mutateStore", (mutation: (state: Draft<SessionStore>) => void) =>
                        set(
                            (state) => {
                                mutation(state);
                            },
                            false,
                            "mutateStore",
                        ),
                    ),
                    addTaskToSession: safeAction("addTaskToSession", (sessionId: string, task: Task) =>
                        set(
                            (state) => {
                                const session = state.sessions.find((s) => s.id === sessionId);
                                if (!session) throw new Error(`Session ${sessionId} not found`);

                                session.addTask(task);
                                session.metadata.status = "active";

                                return state;
                            },
                            false,
                            "addTaskToSession",
                        ),
                    ),
                    addSession: safeAction("addSession", (session: SessionInstance, shouldSelect: boolean = false) =>
                        set(
                            (state) => {
                                logger.trace({ session, shouldSelect }, "addSession()");
                                state.sessions.push(session);
                                if (shouldSelect) {
                                    state.selectedSessionId = session.id;
                                }
                                return state;
                            },
                            false,
                            "addSession",
                        ),
                    ),
                    endSession: safeAction("endSession", (sessionId: string, reason: SessionChangeReason) =>
                        set(
                            (state) => {
                                logger.trace({ sessionId, reason }, "endSession()");

                                if (state.selectedSessionId === sessionId) {
                                    state.selectedSessionId = "";
                                }
                                const session = state.sessions.find((s) => s.id === sessionId);
                                if (!session) throw new Error(`Session ${sessionId} not found`);

                                session.endSession(reason);

                                return state;
                            },
                            false,
                            "endSession",
                        ),
                    ),
                    setSelectedSession: safeAction("setSelectedSession", (sessionId: string) =>
                        set(
                            (state) => {
                                logger.trace({ sessionId }, "selectSession()");
                                const session = state.sessions.find((s) => s.id === sessionId);

                                if (!session && sessionId !== get().idleSession.id) {
                                    throw new Error(`Cannot select session ${sessionId}, not found`);
                                }

                                state.selectedSessionId = sessionId;
                                return state;
                            },
                            false,
                            "selectSession",
                        ),
                    ),
                    clearSelectedSession: safeAction("clearSelectedSession", () =>
                        set(
                            (state) => {
                                logger.trace({ selectedSessionId: state.selectedSessionId }, "clearSelectedSession()");
                                state.selectedSessionId = "";
                                return state;
                            },
                            false,
                            "clearSelectedSession",
                        ),
                    ),
                    updateTaskStatus: safeAction(
                        "updateTaskStatus",
                        (taskId: string, status: TaskStatus, reason?: TaskStatusReason) =>
                            set(
                                (state) => {
                                    logger.trace({ taskId, status, reason }, "updateTaskStatus()");
                                    return updateTaskStatusInternal(state, taskId, status, reason);
                                },
                                false,
                                "updateTaskStatus",
                            ),
                    ),
                    muteTask: safeAction("muteTask", (taskId: string, shouldMute: boolean) =>
                        set(
                            (state) => {
                                logger.trace({ taskId, shouldMute }, "muteTask()");
                                return mutateVoiceTask(state, taskId, (task) => {
                                    task.isMuted = shouldMute;
                                });
                            },
                            false,
                            "muteTask",
                        ),
                    ),
                    setHold: safeAction("setHold", (taskId: string, onHold: boolean) =>
                        set(
                            (state) => {
                                logger.trace({ taskId, onHold }, "setHold()");
                                return mutateVoiceTask(state, taskId, (task) => {
                                    if (onHold && task.holdState.onHold) {
                                        throw new Error("Hold is already true");
                                    }

                                    task.holdState = {
                                        onHold,
                                        onHoldSince: onHold ? Date.now() : null,
                                    };
                                });
                            },
                            false,
                            "setHold",
                        ),
                    ),
                    setStartTimestamp: safeAction("setStartTimestamp", (taskId: string, timestamp: number) =>
                        set(
                            (state) => {
                                logger.trace({ taskId, timestamp }, "setStartTimestamp()");
                                return mutateTrainingTask(state, taskId, (task) => {
                                    task.startTimestamp = timestamp;
                                });
                            },
                            false,
                            "setStartTimestamp",
                        ),
                    ),
                    setWrappingState: safeAction(
                        "setWrappingState",
                        (wrappingState: WrappingState, sessionId: string) =>
                            set(
                                (state) => {
                                    logger.trace({ sessionId, wrappingState }, "setWrappingState()");

                                    const session = state.sessions.find((session) => session.id === sessionId);

                                    if (!session) {
                                        throw new Error("Set Wrapping State | Session not found");
                                    }

                                    if (session.metadata.status !== "active") {
                                        logger.error(
                                            { sessionId, wrappingState },
                                            "Cannot set WrappingState on an inactive session.",
                                        );
                                        throw new Error("Cannot set WrappingState on an inactive session.");
                                    }

                                    session.wrappingState = wrappingState;

                                    return state;
                                },
                                false,
                                "setWrappingState",
                            ),
                    ),
                    clearWrappingState: safeAction("clearWrappingState", (sessionId: string) =>
                        set(
                            (state) => {
                                logger.trace({ sessionId }, "clearWrappingState()");
                                const session = state.sessions.find((session) => session.id === sessionId);

                                if (!session) {
                                    throw new Error("Clear Wrapping State | Session not found");
                                }

                                session.wrappingState = null;

                                return state;
                            },
                            false,
                            "clearWrappingState",
                        ),
                    ),
                    setConferenceStarted: safeAction("setConferenceStarted", (taskId: string, isStarted: boolean) =>
                        set(
                            (state) => {
                                logger.trace({ taskId, isStarted }, "setConferenceStarted()");
                                return mutateVoiceTask(state, taskId, (task) => {
                                    task.conferenceStarted = isStarted;
                                });
                            },
                            false,
                            "setConferenceStarted",
                        ),
                    ),
                    setCustomerLeftTheCall: safeAction("setCustomerLeftTheCall", (taskId: string) =>
                        set(
                            (state) => {
                                logger.trace({ taskId }, "setCustomerLeftTheCall()");
                                return mutateVoiceTask(state, taskId, (task) => {
                                    task.holdState = {
                                        onHold: false,
                                        onHoldSince: null,
                                    };
                                    task.customerLeftTheConference = true;
                                });
                            },
                            false,
                            "setCustomerLeftTheCall",
                        ),
                    ),
                    upsertConferenceParticipant: safeAction(
                        "upsertConferenceParticipant",
                        (taskId: string, participant: ConferenceParticipant) =>
                            set(
                                (state) => {
                                    logger.trace({ taskId, participant }, "upsertConferenceParticipant()");
                                    return mutateVoiceTask(state, taskId, (task) => {
                                        const index = task.conferenceParticipants.findIndex(
                                            (p) => p.participantCallId === participant.participantCallId,
                                        );
                                        if (index === -1) {
                                            task.conferenceParticipants.push(participant);
                                            return;
                                        }
                                        task.conferenceParticipants = task.conferenceParticipants.with(
                                            index,
                                            participant,
                                        );
                                    });
                                },
                                false,
                                "upsertConferenceParticipant",
                            ),
                    ),
                    updateConferenceParticipant: safeAction(
                        "updateConferenceParticipant",
                        (taskId: string, participantCallId: string, options: { hold: boolean }) =>
                            set(
                                (state) => {
                                    logger.trace(
                                        { taskId, participantCallId, options },
                                        "updateConferenceParticipant()",
                                    );
                                    return mutateVoiceTask(state, taskId, (task) => {
                                        const participantIndex = task.conferenceParticipants.findIndex(
                                            (p) => p.participantCallId === participantCallId,
                                        );
                                        if (participantIndex === -1) throw new Error("Participant not found");
                                        const participant = task.conferenceParticipants[participantIndex];
                                        task.conferenceParticipants[participantIndex] = {
                                            ...participant,
                                            hold: options.hold ? { since: Date.now() } : undefined,
                                        };
                                    });
                                },
                                false,
                                "updateConferenceParticipant",
                            ),
                    ),
                    removeConferenceParticipant: safeAction(
                        "removeConferenceParticipant",
                        (taskId: string, participantCallId: string) =>
                            set(
                                (state) => {
                                    logger.trace({ taskId, participantCallId }, "removeConferenceParticipant()");
                                    return mutateVoiceTask(state, taskId, (task) => {
                                        task.conferenceParticipants = task.conferenceParticipants.filter(
                                            (p) => p.participantCallId !== participantCallId,
                                        );
                                    });
                                },
                                false,
                                "removeConferenceParticipant",
                            ),
                    ),

                    resetConferenceState: safeAction(
                        "resetConferenceState",
                        (taskId: string, conferenceDetails: ConferenceDetails) =>
                            set(
                                (state) => {
                                    logger.trace({ taskId }, "resetConferenceState");
                                    return mutateVoiceTask(state, taskId, (task) => {
                                        task.holdState = conferenceDetails.holdState;
                                        task.customerLeftTheConference = conferenceDetails.customerLeftTheConference;
                                        task.conferenceParticipants = conferenceDetails.conferenceParticipants;
                                        task.isMuted = conferenceDetails.isMuted;
                                    });
                                },
                                false,
                                "resetConferenceState",
                            ),
                    ),

                    setTaskKey: <T extends Task, K extends WritableKeysOf<T>>(taskId: string, key: K, value: T[K]) =>
                        set(
                            (state) => {
                                const actionLogger = logger.child({ taskId, taskKey: key });
                                actionLogger.trace("setTaskKey");
                                return mutateTask<T>(
                                    state,
                                    taskId,
                                    (task) => {
                                        task[key] = value;
                                    },
                                    actionLogger,
                                );
                            },
                            false,
                            "setTaskKey",
                        ),

                    resetHistory: safeAction("resetHistory", () => {
                        if (import.meta.env.VITE_ENV_TYPE === "production") {
                            throw new Error("resetHistory() should not be called in production");
                        }

                        set(
                            (state) => {
                                state.sessions = [state.sessions[state.sessions.length - 1]];
                                return state;
                            },
                            false,
                            "clearHistory",
                        );
                    }),
                    clearInactiveSession: (sessionId: string) => {
                        set(
                            (state) => {
                                state.sessions = state.sessions.filter((s) => s.id !== sessionId);

                                if (state.selectedSessionId && state.selectedSessionId === sessionId) {
                                    state.selectedSessionId = "";
                                }

                                return state;
                            },
                            false,
                            "clearInactiveSession",
                        );
                    },
                    resetStore: safeAction("resetStore", () => {
                        if (import.meta.env.VITE_ENV_TYPE === "production") {
                            throw new Error("resetStore() should not be called in production");
                        }

                        set(
                            (state) => {
                                state.sessions = [];
                                state.initialized = false;
                                state.selectedSessionId = "";
                                return state;
                            },
                            false,
                            "resetStore",
                        );
                    }),

                    getTask: (taskId: string) => findTaskById(get(), taskId),
                    getSession: (sessionId: string) =>
                        findSession(get(), (session) => session.id === sessionId) as Session | null,
                    getSessionByTaskId: (taskId: string) =>
                        findSession(get(), (session) => session.tasks.has(taskId)) as Session | null,
                    computed: {
                        selectedSession: () => {
                            const selectedSessionId = get().selectedSessionId;
                            const session = get().sessions.find((s) => s.id === selectedSessionId);
                            return session ?? get().idleSession;
                        },
                        //TODO: Future, actually support multi-sessions with an active session & task
                        selectedTasks: () => {
                            const currentSession = get().computed.selectedSession();
                            return currentSession?.currentTask ? [currentSession?.currentTask] : [];
                        },
                        sessionIds: () => get().sessions.map((s) => s.id),
                        isIdleSession: () => ["", get().idleSession.id].includes(get().selectedSessionId),
                    },
                }),
                { enabled: import.meta.env.VITE_ENV_TYPE !== "production", store: "sessions", name: "workspace/sdk" },
            ),
            {
                name: "session-tasks-store",
                // @ts-expect-error types needs to be fixed to allow partial store return value
                partialize: (state) => ({
                    sessions: state.sessions.slice(-50),
                    selectedSessionId: state.selectedSessionId,
                }),
                storage: {
                    getItem(name: string) {
                        const str = localStorage.getItem(name);
                        if (!str)
                            return {
                                state: {
                                    sessions: [],
                                },
                            } as unknown as StorageValue<SessionStore>;

                        const state = JSON.parse(str, deserializeReplacer) as SessionStoreState;

                        state.sessions = state.sessions.map((session) => new SessionInstance(session));

                        // To achieve the requirement that we do not expose tasks till post SDK/Twilio init
                        // This is restored upon init
                        state.sessions.forEach((session) => {
                            session.hideRehydratedTasks();
                        });

                        return {
                            state,
                        } as StorageValue<SessionStore>;
                    },
                    setItem(name: string, value: StorageValue<SessionStore>) {
                        const str = safeStringify(value.state, serializeReplacer);
                        localStorage.setItem(name, str ?? "");
                    },
                    removeItem: (name: string) => {
                        localStorage.removeItem(name);
                    },
                },
            },
        ),
    ),
);

function mutateVoiceTask(state: Draft<SessionStore>, taskId: string, mutation: (task: Draft<VoiceTask>) => void) {
    const task = findTaskById(state, taskId);
    if (!task) throw new Error(`${taskId} not found in store`);
    if (!isVoiceTask(task)) throw new Error(`${taskId} isn't a voice task`);

    mutation(task);

    return state;
}

function mutateTrainingTask(state: Draft<SessionStore>, taskId: string, mutation: (task: Draft<TrainingTask>) => void) {
    const task = findTaskById(state, taskId);
    if (!task) throw new Error(`${taskId} not found in store`);
    if (!isTrainingTask(task)) throw new Error(`${taskId} isn't a training task`);

    mutation(task);

    return state;
}

function mutateTask<TaskType extends Task>(
    state: Draft<SessionStore>,
    taskId: string,
    mutation: (task: TaskType) => void,
    parentLogger: Logger,
) {
    parentLogger.trace({ taskId }, "mutateTask");
    const task = findTaskById(state, taskId);
    if (!task) {
        logger.error({ taskId }, "Task not found in store, skipping mutateTask");
        return state;
    }
    parentLogger.debug(task.toLog(), "Execute mutateTask");
    mutation(task as TaskType);

    return state;
}

/** Should not be exposed */
export function updateTaskStatusInternal(
    state: Draft<SessionStore>,
    taskId: string,
    status: TaskStatus,
    reason?: TaskStatusReason,
) {
    const existingTask = findTaskById(state, taskId);
    if (!existingTask) throw new Error(`${taskId} not found in store`);

    existingTask.status = status;
    existingTask.statusReason = reason;
    existingTask.statusUpdatedAt = new Date();

    //TODO: Pre-existing duplicated logic, perhaps the below can be removed for every state change?
    resetOnWrapping(existingTask);

    //TODO: Why do we do this 👇...?
    if (isVoiceTask(existingTask)) {
        existingTask.isMuted = false;
        existingTask.holdState = { onHold: false, onHoldSince: null };
    }

    return state;
}

function resetOnWrapping(task: Draft<Task>) {
    if (isVoiceTask(task)) {
        task.isMuted = false;
        task.holdState = { onHold: false, onHoldSince: null };
    }
}

function findTaskById(state: Draft<SessionStore>, taskId: string) {
    return (() => {
        const session = state.sessions.find(({ tasks }) => tasks.has(taskId));
        const task = session?.tasks.get(taskId);
        return task ?? null;
    })();
}

// TODO: do we need this still?
function findSession(state: Draft<SessionStore>, predicate: (session: Session) => boolean) {
    return (() => {
        // TODO: remove?
        // Reverse search for performance & correctness
        for (let i = state.sessions.length - 1; i >= 0; i--) {
            const session = state.sessions[i];
            if (predicate(session as Session)) return session;
        }

        return null;
    })();
}

function serializeReplacer(key: string, value: unknown) {
    if (key.startsWith("_")) return undefined;

    if ((key === "tasks" || key === "rehydratedTasks") && value instanceof Map) {
        return Array.from(value.entries());
    }

    return value;
}

function deserializeReplacer(key: string, value: unknown) {
    if ((key === "tasks" || key === "rehydratedTasks") && Array.isArray(value)) {
        return new Map(value);
    }

    return value;
}
